import React, { useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Button, IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import type { IncidentGroup } from '../../services/incidentsApi';
import type { IncidentGroupPatternActionMode } from './IncidentGroupPatternActionDialog';

export type IncidentGroupPatternActionsProps = {
  group: IncidentGroup;
  canManage: boolean;
  busy?: boolean;
  onAction: (group: IncidentGroup, mode: IncidentGroupPatternActionMode) => void;
  compact?: boolean;
};

const IncidentGroupPatternActions: React.FC<IncidentGroupPatternActionsProps> = ({
  group,
  canManage,
  busy = false,
  onAction,
  compact = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const count = Math.max(0, Number(group.alert_count || 0));
  if (!canManage) return null;

  const stop = (event: React.MouseEvent, mode: IncidentGroupPatternActionMode) => {
    event.stopPropagation();
    onAction(group, mode);
  };

  const closeMenu = () => setMenuAnchor(null);

  if (compact) {
    return (
      <>
        <IconButton
          size="small"
          aria-label="More actions"
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
        >
          <MoreHorizIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          onClick={(event) => event.stopPropagation()}
        >
          <MenuItem
            disabled={busy}
            onClick={(event) => {
              stop(event, 'assign');
              closeMenu();
            }}
          >
            <ListItemText primary="Assign owner" secondary="Apply across open linked records" />
          </MenuItem>
          <MenuItem
            disabled={busy}
            onClick={(event) => {
              stop(event, 'suppress');
              closeMenu();
            }}
          >
            <ListItemText primary="Suppress all" secondary="Mute similar triggers for 8 hours" />
          </MenuItem>
          <MenuItem
            disabled={busy}
            onClick={(event) => {
              stop(event, 'close');
              closeMenu();
            }}
          >
            <ListItemText
              primary="Close recurring risk"
              secondary={`Close all ${count} open linked records`}
            />
          </MenuItem>
        </Menu>
      </>
    );
  }

  const size = 'small';
  const sx = { fontWeight: 700 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, alignItems: 'center' }}>
      <Tooltip title={`Close all ${count} open linked records in this recurring risk`}>
        <span>
          <Button size={size} variant="outlined" color="success" sx={sx} disabled={busy} onClick={(e) => stop(e, 'close')}>
            Close recurring risk
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Mute similar triggers for 8 hours (scope from recurring risk)">
        <span>
          <Button size={size} variant="outlined" sx={sx} disabled={busy} onClick={(e) => stop(e, 'suppress')}>
            Suppress all
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Assign risk owner across open linked records (not Odoo)">
        <span>
          <Button size={size} variant="outlined" sx={sx} disabled={busy} onClick={(e) => stop(e, 'assign')}>
            Assign owner
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default IncidentGroupPatternActions;
